/**
 * DonationCaptureSucceeded.tsx - Donation Success Confirmation Step
 *
 * This component displays the final success step after a donation has been
 * processed successfully with receipt information and sharing options.
 *
 * Key Features:
 * - Donation confirmation with receipt details
 * - Thank you message and impact information
 * - Receipt download and email options
 * - Social sharing integration
 * - Additional donation encouragement
 * - Navigation options for next steps
 *
 * Success Information:
 * - Donation amount and transaction details
 * - Donor information confirmation
 * - Transaction ID and timestamp
 * - Tax receipt information (if applicable)
 *
 * Post-Donation Actions:
 * - Download receipt as PDF
 * - Email receipt to donor
 * - Share donation on social media
 * - Make another donation
 * - Return to fundraiser page
 *
 * Props:
 * - donationData: DonationData - Completed donation information
 * - onClose: () => void - Close success modal handler
 * - onNewDonation: () => void - Start new donation handler
 * - onDownloadReceipt: () => void - Download receipt handler
 *
 * Dependencies: Social sharing modal, receipt generation, styled-components
 */

import React from "react";
import styled from "styled-components";
import { Heart, Download } from "lucide-react";
import {
  buildReceiptUrl,
  IAPIPaymentReceiptViewModel,
} from "@mindmemobile/mm-payment-modules";
import PaymentSuccessImage from "../../images//payment-succeeded.svg";
import { SocialShareModal } from "../SocialShareModal";
import { fundraiserStore } from "../../stores";
const SuccessContainer = styled.div<{ isEmbedded: boolean }>`
  background: #ffffff;
  border-radius: ${(props) => (props.isEmbedded ? "16px" : "24px")};
  width: 100%;
  max-width: ${(props) => (props.isEmbedded ? "100%" : "600px")};
  min-height: ${(props) => (props.isEmbedded ? "fit-content" : "fit-content")};
  max-height: ${(props) => (props.isEmbedded ? "none" : "calc(90vh + 30px)")};
  overflow-y: ${(props) => (props.isEmbedded ? "visible" : "auto")};
  position: relative;
  box-shadow: ${(props) =>
    props.isEmbedded
      ? "0 4px 20px rgba(0, 0, 0, 0.08)"
      : "0 20px 60px rgba(0, 0, 0, 0.3)"};
  border: ${(props) =>
    props.isEmbedded
      ? "1px solid #f1f5f9"
      : "1px solid rgba(255, 255, 255, 0.2)"};
  display: flex;
  flex-direction: column;
  padding: ${(props) => (props.isEmbedded ? "0" : "0")};
`;

const CloseButton = styled.button<{ isEmbedded: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: ${(props) => (props.isEmbedded ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SuccessTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #10b981;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
`;

const SuccessContent = styled.div<{ isEmbedded?: boolean }>`
  padding: ${(props) => (props.isEmbedded ? "16px 32px" : "16px 32px")};
  display: flex;
  flex-direction: column;
`;

const HeaderSection = styled.div<{ isEmbedded: boolean }>`
  background: ${(props) => (props.isEmbedded ? "#f0fdf4" : "#f0fdf4")};
  border-radius: ${(props) =>
    props.isEmbedded ? "16px 16px 0 0" : "24px 24px 0 0"};
  padding: ${(props) => (props.isEmbedded ? "32px 32px" : "24px 32px")};
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AmountSection = styled.div<{ isEmbedded: boolean }>`
  text-align: center;
  margin-bottom: 29px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const AmountValue = styled.div`
  font-size: 48px;
  font-weight: 800;
  color: #10b981;
`;

const SuccessIcon = styled.img`
  width: 130px;
  height: auto;
`;

const DetailsGrid = styled.div<{ isEmbedded: boolean }>`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px 40px;
  margin-bottom: 24px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  text-align: right;
`;

const DetailValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionsContainer = styled.div<{ isEmbedded: boolean }>`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 14px 0 24px 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: #ffffff;
  border: 2px solid #10b981;
  color: #10b981;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    border-color: #059669;
  }

  &:active {
    transform: translateY(0);
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const FooterContainer = styled.div<{ isEmbedded: boolean }>`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: ${(props) => (props.isEmbedded ? "0 0 20px 0" : "24px 0 36px 0")};
  border-top: ${(props) => (props.isEmbedded ? "none" : "1px solid #f1f5f9")};
  margin-top: ${(props) => (props.isEmbedded ? "9px" : "1px")};
`;

const CloseModalButton = styled.button<{ isEmbedded: boolean }>`
  padding: 16px 32px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: none;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MakeAnotherDonationButton = styled.button`
  padding: 16px 32px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ShareButton = styled.button`
  padding: 16px 32px;
  background: #10b981;
  color: #ffffff;
  border: 2px solid #10b981;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #059669;
    border-color: #059669;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
const LegalText = styled.div<{ isEmbedded: boolean }>`
  font-size: 14px;
  color: #64748b;
  text-align: center;
  line-height: 1.5;
  margin: 6px auto -4px auto;
  max-width: ${(props) => (props.isEmbedded ? "400px" : "100%")};
  padding-bottom: 5px;
`;

interface DonationData {
  amount: number;
  frequency: "one-time" | "monthly";
  donorInfo: {
    firstName: string;
    lastName: string;
    streetAddress: string;
    zip: string;
    city: string;
    state: string;
    country: string;
    email: string;
    phone: string;
  };
  // paymentMethod: "card" | "bank";
  // paymentInfo: {
  //   cardNumber: string;
  //   accountHolderName: string;
  // };
}

interface DonationCaptureSucceededProps {
  donationData: DonationData;
  onClose: () => void;
  onMakeAnotherDonation: () => void;
  legalInfo: {
    legalName?: string;
    email?: string;
    phone?: string;
  };
  isEmbedded?: boolean;
  shortUniqueId: string;
  /**
   * The receipt the pay API returned for this donation - the same content the confirmation email
   * rendered. Null when the API returned none (older API / receipt could not be built), in which
   * case "Download Receipt" falls back to assembling the card from the data on this screen.
   */
  receipt?: IAPIPaymentReceiptViewModel | null;
}

export const DonationCaptureSucceeded: React.FC<
  DonationCaptureSucceededProps
> = ({
  donationData,
  onClose,
  onMakeAnotherDonation,
  legalInfo,
  shortUniqueId,
  isEmbedded = false,
  receipt = null,
}) => {
  const [showShareModal, setShowShareModal] = React.useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const generateOrderId = () => {
    return `Ord_${Date.now().toString().slice(-6)}`;
  };

  const getFormattedAddress = () => {
    return `123 Main Street\nSuite 101\nPhiladelphia, PA 19046\nUnited States`;
  };

  // const getPaymentMethodDisplay = () => {
  //   if (donationData.paymentMethod === "card") {
  //     return (
  //       <PaymentMethodContainer>
  //         <img src="/visa-icon.svg" alt="Visa" width={32} height={20} />
  //         <span>...1234</span>
  //       </PaymentMethodContainer>
  //     );
  //   } else {
  //     return (
  //       <PaymentMethodContainer>
  //         <img src="/bank-icon.svg" alt="Bank" width={64} height={40} />
  //         <span>...123456</span>
  //       </PaymentMethodContainer>
  //     );
  //   }
  // };

  const getLegalText = () => {
    const { legalName, email, phone } = legalInfo;
    const displayName = legalName;

    let contactInfo = "";
    if (email && phone) {
      contactInfo = `at ${email} or ${phone}`;
    } else if (email) {
      contactInfo = `at ${email}`;
    } else if (phone) {
      contactInfo = `at ${phone}`;
    } else {
      // contactInfo = "at [email] or [phone]";
    }

    return displayName
      ? `${displayName} will be displayed on your credit card / bank statement.\nContact us with questions ${contactInfo}.`
      : "";
  };

  const handleRequestRefund = () => {
    // Handle refund request logic here
    console.log("Request refund clicked");
  };

  const handleDownloadReceipt = () => {
    // Open the public receipt page - the same URL the receipt email's Download PDF button points at.
    // That page renders the v2 design and prints it to PDF, so the emailed receipt, this button and
    // the printed PDF are all one design. Deliberately NO client-side PDF fallback: the previous one
    // drew the older receipt layout, so an unset ReceiptBaseUrl silently produced a receipt that did
    // not match the design at all.
    const receiptUrl = buildReceiptUrl(receipt);
    if (!receiptUrl) {
      console.error("No receipt URL or token on the donation response.");
      return;
    }
    window.open(receiptUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <SuccessContainer isEmbedded={isEmbedded}>
      <HeaderSection isEmbedded={isEmbedded}>
        <SuccessTitle>
          <Heart size={28} color="#10b981" />
          Thank you for your donation
        </SuccessTitle>
      </HeaderSection>

      <SuccessContent>
        <AmountSection isEmbedded={isEmbedded}>
          <AmountValue>{formatCurrency(donationData.amount)}</AmountValue>
          <SuccessIcon src={PaymentSuccessImage} alt="Payment Succeeded" />
        </AmountSection>

        <DetailsGrid isEmbedded={isEmbedded}>
          <DetailLabel>Name</DetailLabel>
          <DetailValue>
            {donationData.donorInfo?.firstName}{" "}
            {donationData.donorInfo?.lastName}
          </DetailValue>

          {/* <DetailLabel>Order</DetailLabel>
            <DetailValue>{generateOrderId()}</DetailValue> */}

          <DetailLabel>Date</DetailLabel>
          <DetailValue>{formatDate(new Date())}</DetailValue>

          <DetailLabel>Amount</DetailLabel>
          <DetailValue>{formatCurrency(donationData.amount)} USD</DetailValue>

          <DetailLabel>Frequency</DetailLabel>
          <DetailValue>
            {donationData.frequency === "monthly" ? "Monthly" : "One Time"}
          </DetailValue>

          {/* <DetailLabel>Payment Method</DetailLabel>
            <DetailValue>{getPaymentMethodDisplay()}</DetailValue> */}
        </DetailsGrid>

        <ActionsContainer isEmbedded={isEmbedded}>
          <ActionButton onClick={handleDownloadReceipt}>
            <Download size={20} color="#12B981" />
            Download Receipt
          </ActionButton>
        </ActionsContainer>

        <LegalText isEmbedded={isEmbedded}>{getLegalText()}</LegalText>
      </SuccessContent>

      <FooterContainer isEmbedded={isEmbedded}>
        <CloseModalButton isEmbedded={isEmbedded} onClick={onClose}>
          Close
        </CloseModalButton>
        <MakeAnotherDonationButton onClick={onMakeAnotherDonation}>
          Make Another Donation
        </MakeAnotherDonationButton>
        <ShareButton onClick={() => setShowShareModal(true)}>Share</ShareButton>
      </FooterContainer>
      <SocialShareModal
        isVisible={showShareModal}
        gallerySettings={fundraiserStore.gallerySettings}
        onClose={() => setShowShareModal(false)}
        shortUniqueId={shortUniqueId}
      />
    </SuccessContainer>
  );
};
